import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertCircle,
  Download,
  FileText,
  Loader2,
  RefreshCw,
  Shield,
  Upload,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATO_VALIDITA = {
  mancante: { label: "Mancante", className: "border-amber-200 bg-amber-50 text-amber-800" },
  scaduto: { label: "Scaduto", className: "border-red-200 bg-red-50 text-red-800" },
  in_scadenza: { label: "In scadenza", className: "border-orange-200 bg-orange-50 text-orange-800" },
  valido: { label: "Valido", className: "border-emerald-200 bg-emerald-50 text-emerald-800" },
  senza_scadenza: { label: "Senza scadenza", className: "border-slate-200 bg-slate-50 text-slate-700" },
  valido_fino_a_revoca: { label: "Fino a revoca", className: "border-slate-200 bg-slate-50 text-slate-700" },
};

const CLASSE_OPTS = [
  { id: "senza_scadenza", label: "Senza scadenza" },
  { id: "con_scadenza", label: "Con scadenza" },
  { id: "valido_fino_a_revoca", label: "Valido fino a revoca" },
];

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

async function downloadBlob(url, token, fallbackName) {
  const res = await axios.get(url, {
    headers: authHeaders(token),
    responseType: "blob",
  });
  const cd = res.headers["content-disposition"] || "";
  const m = /filename="?([^"]+)"?/i.exec(cd);
  const name = m?.[1] || fallbackName;
  const blobUrl = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

function DocRow({ doc, token, busy, onUpload, onPatch, onRefresh }) {
  const fileRef = useRef(null);
  const [classe, setClasse] = useState(doc.classe_validita || "senza_scadenza");
  const [scadenza, setScadenza] = useState(doc.scadenza || "");
  const meta = STATO_VALIDITA[doc.stato_validita] || STATO_VALIDITA.mancante;
  const modelloOk = doc.modello && !doc.modello.mancante && doc.modello.id;
  const rowBusy = busy === doc.tipo;

  useEffect(() => {
    setClasse(doc.classe_validita || "senza_scadenza");
    setScadenza(doc.scadenza || "");
  }, [doc.id, doc.classe_validita, doc.scadenza]);

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium text-slate-900">{doc.tipo_label || doc.tipo}</p>
          {doc.mancante || !doc.id ? (
            <p className="mt-1 text-xs text-slate-500">
              Nessun documento firmato. Scarica il modello, firma fuori Affidya e ricarica il file.
            </p>
          ) : (
            <dl className="mt-1 space-y-0.5 text-xs text-slate-600">
              <div>
                <span className="text-slate-400">File: </span>
                {doc.file_name}
                {doc.version ? ` · v${doc.version}` : ""}
              </div>
              <div>
                <span className="text-slate-400">Firma: </span>
                {doc.stato_firma || "—"}
                {doc.created_by_role ? ` · caricato da ${doc.created_by_role}` : ""}
              </div>
              <div>
                <span className="text-slate-400">Data: </span>
                {doc.data_documento || "—"}
                {doc.scadenza ? ` · Scad. ${doc.scadenza}` : ""}
              </div>
            </dl>
          )}
        </div>
        <Badge variant="outline" className={`text-[10px] ${meta.className}`}>
          {meta.label}
        </Badge>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!modelloOk || rowBusy}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          onClick={async () => {
            try {
              await downloadBlob(
                `${API_URL}/api/deblistering/contratti-privacy/${encodeURIComponent(doc.tipo)}/modello`,
                token,
                `modello_${doc.tipo}.pdf`,
              );
            } catch (e) {
              toast.error(e.response?.data?.detail || "Modello non disponibile");
            }
          }}
        >
          <Download className="h-3.5 w-3.5" />
          Scarica modello
        </button>

        {doc.id ? (
          <button
            type="button"
            disabled={rowBusy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            onClick={async () => {
              try {
                await downloadBlob(
                  `${API_URL}/api/deblistering/contratti-privacy/${encodeURIComponent(doc.tipo)}/download`,
                  token,
                  doc.file_name || `${doc.tipo}.pdf`,
                );
              } catch (e) {
                toast.error(e.response?.data?.detail || "Download non riuscito");
              }
            }}
          >
            <Download className="h-3.5 w-3.5" />
            Scarica firmato
          </button>
        ) : null}

        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-medium text-cyan-800 hover:bg-cyan-100">
          {rowBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {doc.id ? "Ricarica firmato" : "Carica firmato"}
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,application/pdf,image/*"
            disabled={rowBusy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) onUpload(doc.tipo, f, { classe_validita: classe, scadenza });
            }}
          />
        </label>
      </div>

      {!modelloOk ? (
        <p className="mt-2 text-[11px] text-amber-700">
          Modello blank non ancora caricato dalla farmacia collegata.
        </p>
      ) : null}

      {doc.id ? (
        <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-slate-200 pt-3">
          <div>
            <label className="mb-1 block text-[11px] font-medium text-slate-600">Validità</label>
            <select
              value={classe}
              onChange={(e) => setClasse(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs"
            >
              {CLASSE_OPTS.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
          {classe === "con_scadenza" ? (
            <div>
              <label className="mb-1 block text-[11px] font-medium text-slate-600">Scadenza</label>
              <input
                type="date"
                value={scadenza || ""}
                onChange={(e) => setScadenza(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs"
              />
            </div>
          ) : null}
          <button
            type="button"
            disabled={rowBusy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-900 disabled:opacity-50"
            onClick={() => onPatch(doc.id, {
              classe_validita: classe,
              scadenza: classe === "con_scadenza" ? scadenza : null,
            })}
          >
            Salva validità
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            onClick={onRefresh}
          >
            <RefreshCw className="h-3 w-3" />
            Aggiorna
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function ContrattiPrivacyStruttura({ token }) {
  const { activeStruttura } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/deblistering/contratti-privacy`, {
        headers: authHeaders(token),
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento contratti/privacy");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, activeStruttura?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- refetch al cambio struttura

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (tipo, file, meta = {}) => {
    setBusy(tipo);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("stato_firma", "firmato");
      fd.append("classe_validita", meta.classe_validita || "senza_scadenza");
      if (meta.classe_validita === "con_scadenza" && meta.scadenza) {
        fd.append("scadenza", meta.scadenza);
      }
      await axios.post(
        `${API_URL}/api/deblistering/contratti-privacy/${encodeURIComponent(tipo)}`,
        fd,
        { headers: authHeaders(token) },
      );
      toast.success("Documento firmato caricato e condiviso con la farmacia");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload");
    } finally {
      setBusy(null);
    }
  };

  const handlePatch = async (docId, patch) => {
    setBusy(docId);
    try {
      await axios.patch(
        `${API_URL}/api/deblistering/contratti-privacy/${encodeURIComponent(docId)}`,
        patch,
        { headers: authHeaders(token) },
      );
      toast.success("Validità aggiornata");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setBusy(null);
    }
  };

  const farm = data?.farmacia_collegata;
  const alert = data?.alert;

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Shield className="w-5 h-5 text-cyan-600" />
          Contratti e Privacy Struttura
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-500">
          Contratto di servizio e nomina privacy (GDPR) firmati e condivisi con la farmacia
          collegata al Portale. Scarica il modello, firma fuori Affidya e ricarica il file
          (come per i PAI). Puoi impostare una scadenza annuale se serve.
        </p>

        {loading && !data ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-cyan-600" />
          </div>
        ) : !farm ? (
          <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-medium">Nessuna farmacia collegata al Portale</p>
              <p className="mt-1 text-amber-800/90">
                {alert?.messaggio ||
                  "I Contatti Farmacia sopra non bastano: serve il collegamento amministrativo Portale Farmacia ↔ struttura."}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              <div className="flex items-center gap-2 text-slate-700">
                <FileText className="h-4 w-4 text-slate-400" />
                <span>
                  Farmacia collegata: <strong>{farm.nome || farm.email || farm.id}</strong>
                </span>
              </div>
              <button
                type="button"
                onClick={load}
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Aggiorna
              </button>
            </div>

            {alert?.has_alert ? (
              <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>{alert.messaggio || "Documenti mancanti o da aggiornare."}</p>
              </div>
            ) : (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                Contratto e privacy presenti e in regola.
              </div>
            )}

            <div className="grid gap-3 md:grid-cols-2">
              {(data?.items || []).map((doc) => (
                <DocRow
                  key={doc.tipo}
                  doc={doc}
                  token={token}
                  busy={busy}
                  onUpload={handleUpload}
                  onPatch={handlePatch}
                  onRefresh={load}
                />
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
